import { useState } from 'react'
import { followUpAt, type FollowUpKey } from '@sac/contracts'
import { Icon } from '../icons'

/**
 * Asked right after a reply that deliberately did NOT close the conversation.
 *
 * "Enviar" without closing means the thread is still owed something — usually the customer's
 * answer. Nothing in the inbox tracks that: an answered thread is neither unread nor overdue, so
 * it drops out of view and is genuinely forgotten. The moment to decide when to come back is the
 * moment the reply goes out, not later.
 *
 * "Não relembrar" is a real option and not a dismissal to be discouraged: plenty of replies
 * genuinely end the matter without closing it.
 */

export interface FollowUpChoice {
  dueAt: Date | null
  label: string
}

/** Wording here, arithmetic in contracts — see followUpAt for why they are apart. */
const OPTIONS: { key: FollowUpKey; label: string }[] = [
  { key: '1h', label: 'Em 1 hora' },
  { key: '2h', label: 'Em 2 horas' },
  { key: 'tomorrow_first', label: 'Amanhã no primeiro horário' },
  { key: 'tomorrow_same', label: 'Amanhã no mesmo horário' },
]

/** `datetime-local` wants local wall-clock time, which toISOString would shift by the offset. */
export function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function FollowUpPrompt({
  onChoose,
  onDismiss,
}: {
  onChoose: (choice: FollowUpChoice) => void
  onDismiss: () => void
}) {
  const now = new Date()
  const [custom, setCustom] = useState('')
  const [picking, setPicking] = useState(false)
  const options = OPTIONS.map((o) => ({ ...o, dueAt: followUpAt(now, o.key) }))

  const fmt = (d: Date) =>
    d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

  return (
    <div className="modal-back" onClick={onDismiss}>
      <div className="modal fup" onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <Icon name="clock" size={15} />
          Retomar esta conversa quando?
          <button className="icon-btn" onClick={onDismiss} title="Fechar">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="modal-b">
          <p className="modal-note">
            A resposta foi enviada e a conversa segue aberta. Um lembrete a traz de volta se o
            cliente não responder.
          </p>

          <div className="fup-opts">
            {options.map((o) => (
              <button key={o.key} className="fup-opt" onClick={() => onChoose({ dueAt: o.dueAt, label: o.label })}>
                <span className="t">{o.label}</span>
                {/* The resolved date is shown, not just the phrase: "amanhã no mesmo horário"
                    is easy to read one way and mean another. */}
                <span className="d">{fmt(o.dueAt)}</span>
              </button>
            ))}

            {picking ? (
              <div className="fup-custom">
                <input
                  type="datetime-local"
                  autoFocus
                  value={custom}
                  min={toLocalInputValue(now)}
                  onChange={(e) => setCustom(e.target.value)}
                />
                <button
                  className="btn pri sm"
                  disabled={!custom || new Date(custom).getTime() <= now.getTime()}
                  onClick={() => onChoose({ dueAt: new Date(custom), label: 'Data escolhida' })}
                >
                  Definir
                </button>
              </div>
            ) : (
              <button className="fup-opt" onClick={() => { setPicking(true); setCustom(toLocalInputValue(options[3]!.dueAt)) }}>
                <span className="t">Escolher data e hora…</span>
              </button>
            )}
          </div>

          <div className="modal-f">
            <button className="btn ghost sm" onClick={() => onChoose({ dueAt: null, label: 'Não relembrar' })}>
              Não relembrar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
